// Progress Monitoring Component
import React, { useState, useEffect } from 'react';
import { Card, Button, Select, Progress, Row, Col, message } from 'antd';
import { 
  CheckCircleOutlined, 
  ArrowUpOutlined,
  DownloadOutlined,
  CheckOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { useConfirmModal } from '@/hooks/useConfirmModal';
import { ConfirmModal } from '@/index';
import { deviceModelsAPI } from '@/services/api/device-models';
import { versionsAPI } from '@/services/api/versions';
import { statsAPI } from '@/services/api/stats';
import type {
  DeviceModel,
  Version,
  VersionProgressStat,
  VersionTrendStat,
  VersionErrorDistributionStat,
  RolloutProgressStat,
} from '@/types/api';

interface VersionProgress {
  version: string;
  status: 'publishing' | 'paused' | 'completed';
  currentPhase: number;
  totalPhases: number;
  progress: number;
  startTime: string;
  duration: string;
  errorRate: number;
  totalDevices: number;
  downloadSuccessRate: number;
  installSuccessRate: number;
}

interface Phase {
  phase: number;
  percentage: number;
  status: 'completed' | 'current' | 'pending';
  startTime?: string;
  description: string;
}

interface TrendData {
  time: string;
  checkCount: number;
  downloadCount: number;
  installCount: number;
}

interface ErrorData {
  name: string;
  value: number;
  color: string;
  [key: string]: any;
}

const ProgressMonitoring: React.FC = () => {
  const [selectedModel, setSelectedModel] = useState<number | undefined>(undefined);
  const [selectedVersion, setSelectedVersion] = useState<string | undefined>(undefined);
  const [selectedVersionId, setSelectedVersionId] = useState<number | undefined>(undefined);
  const [deviceModels, setDeviceModels] = useState<DeviceModel[]>([]);
  const [versions, setVersions] = useState<
    Array<{ value: string; label: string; id: number; status: string }>
  >([]);
  const [versionProgress, setVersionProgress] = useState<VersionProgressStat | null>(null);
  const [trendStat, setTrendStat] = useState<VersionTrendStat | null>(null);
  const [errorStat, setErrorStat] = useState<VersionErrorDistributionStat | null>(null);
  const [rolloutStat, setRolloutStat] = useState<RolloutProgressStat | null>(null);
  const { config, showConfirmModal, hideConfirmModal, handleConfirm, handleCancel } = useConfirmModal();

  // 获取机型列表
  useEffect(() => {
    const fetchDeviceModels = async () => {
      try {
        const response = await deviceModelsAPI.getDeviceModels({
          page: 1,
          size: 100,
          status: 'in_production', // 只获取生产中的机型
        });
        if (response.list && response.list.length > 0) {
          setDeviceModels(response.list);
          // 默认选择第一个机型
          if (!selectedModel && response.list.length > 0) {
            setSelectedModel(response.list[0].id);
          }
        }
      } catch (error) {
        console.error('获取机型列表失败:', error);
        // 使用模拟数据
        setDeviceModels([
          { id: 1, name: 'Box-A1', chip_vendor: 'Amlogic', chip_model: 'S905X4', os: 'Android TV', os_version: '11.0', status: 'in_production', device_count: 0, created_at: '', updated_at: '' },
          { id: 2, name: 'Box-B2', chip_vendor: 'Amlogic', chip_model: 'S905X3', os: 'Android TV', os_version: '10.0', status: 'in_production', device_count: 0, created_at: '', updated_at: '' },
          { id: 3, name: 'Box-C3', chip_vendor: 'Rockchip', chip_model: 'RK3566', os: 'Android TV', os_version: '11.0', status: 'in_production', device_count: 0, created_at: '', updated_at: '' },
        ]);
        if (!selectedModel) {
          setSelectedModel(1);
        }
      }
    };
    fetchDeviceModels();
  }, []);

  // 当选择机型时，加载该机型的版本列表（真实接口）
  useEffect(() => {
    if (selectedModel) {
      const fetchVersions = async () => {
        try {
          const res = await versionsAPI.getVersions({
            model_id: selectedModel,
            page: 1,
            size: 100,
          });
          const opts =
            (res.list || []).map((v: Version) => {
              let statusLabel = '';
              switch (v.status) {
                case 'draft':
                  statusLabel = '草稿';
                  break;
                case 'testing':
                  statusLabel = '测试中';
                  break;
                case 'publishing':
                  statusLabel = '发布中';
                  break;
                case 'paused':
                  statusLabel = '已暂停';
                  break;
                case 'offline':
                  statusLabel = '已下线';
                  break;
                default:
                  statusLabel = v.status || '';
              }
              return {
                value: v.version,
                id: v.id,
                status: v.status,
                label: `${v.version}${statusLabel ? ` (${statusLabel})` : ''}`,
              };
            }) ?? [];
          setVersions(opts);
          if (!selectedVersion && opts.length > 0) {
            setSelectedVersion(opts[0].value);
            setSelectedVersionId(opts[0].id);
          }
        } catch (error: any) {
          console.error('获取版本列表失败:', error);
          message.error(error?.message || '获取版本列表失败');
          setVersions([]);
          setSelectedVersion(undefined);
          setSelectedVersionId(undefined);
        }
      };
      fetchVersions();
    } else {
      setVersions([]);
      setSelectedVersion(undefined);
      setSelectedVersionId(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedModel]);

  // 根据统计数据组装页面展示模型
  const versionData: VersionProgress = {
    version: selectedVersion || '-',
    status: 'publishing',
    currentPhase: rolloutStat?.currentStage || 0,
    totalPhases: rolloutStat?.stages?.length || 0,
    progress: rolloutStat?.currentPercentage || 0,
    startTime: '',
    duration: '',
    errorRate:
      errorStat && versionProgress && versionProgress.hitDeviceTotal
        ? Number(
            ((errorStat.totalErrors / Math.max(versionProgress.hitDeviceTotal, 1)) * 100).toFixed(1),
          )
        : 0,
    totalDevices: versionProgress?.hitDeviceTotal || 0,
    downloadSuccessRate: versionProgress?.downloadSuccessRate || 0,
    installSuccessRate: versionProgress?.installSuccessRate || 0,
  };

  const phases: Phase[] =
    rolloutStat?.stages?.map((s) => ({
      phase: s.stageIndex,
      percentage: s.percentage,
      status:
        s.status === 'completed'
          ? 'completed'
          : s.status === 'in_progress'
          ? 'current'
          : 'pending',
      startTime: s.completedTime,
      description:
        s.description ||
        (s.status === 'completed'
          ? '已完成'
          : s.status === 'in_progress'
          ? '进行中'
          : '等待中'),
    })) || [];

  // 趋势图数据
  const trendData: TrendData[] =
    trendStat?.points?.map((p) => ({
      time: p.time, // 后端是 ISO 时间字符串，前端可以按需格式化
      checkCount: p.checkCount,
      downloadCount: p.downloadCount,
      installCount: p.installCount,
    })) || [];

  // 错误类型分布数据
  const errorData: ErrorData[] =
    errorStat?.distributions?.map((d, index) => ({
      name: d.typeName,
      value: d.percent,
      color: ['#ff4d4f', '#ff7a45', '#ffec3d', '#9254de', '#8c8c8c'][index % 5],
    })) || [];

  // 当机型和版本确定后，拉取进度/趋势/错误/灰度统计
  useEffect(() => {
    const fetchStats = async () => {
      if (!selectedModel || !selectedVersion) return;
      try {
        const [vp, vt, ve, rp] = await Promise.all([
          statsAPI.getVersionProgress(selectedModel, selectedVersion),
          statsAPI.getVersionTrend(selectedModel, selectedVersion),
          statsAPI.getVersionErrorDistribution(selectedModel, selectedVersion),
          statsAPI.getRolloutProgress(selectedModel, selectedVersion),
        ]);
        setVersionProgress(vp);
        setTrendStat(vt);
        setErrorStat(ve);
        setRolloutStat(rp);
      } catch (error: any) {
        console.error('获取进度统计失败:', error);
        message.error(error?.message || '获取进度统计失败');
      }
    };
    fetchStats();
  }, [selectedModel, selectedVersion]);


  const getPhaseIcon = (status: string, phase: number) => {
    switch (status) {
      case 'completed':
        return (
          <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: 'var(--color-success)' }}>
            <CheckCircleOutlined style={{ color: 'var(--text-inverse)', fontSize: '16px' }} />
          </div>
        );
      case 'current':
        return (
          <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold" style={{ background: 'var(--color-primary)', color: 'var(--text-inverse)' }}>
            {phase}
          </div>
        );
      case 'pending':
        return (
          <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold" style={{ background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }}>
            {phase}
          </div>
        );
      default:
        return null;
    }
  };


  // 处理推进阶段
  const handleAdvancePhase = () => {
    if (!selectedVersionId) {
      message.warning('请先选择版本');
      return;
    }
    showConfirmModal('advance', {
      data: { version: versionData.version },
      advanceConfig: {
        currentPercentage: versionData.progress,
        nextPercentage: 50, // 下一阶段的目标百分比
        hintText: `当前阶段为${versionData.progress}%，推进后将为50%。`,
      },
      onConfirm: async () => {
        try {
          const updated = await versionsAPI.advanceRollout(selectedVersionId);
          setRolloutStat(updated);
          message.success('已推进到下一阶段');
        } catch (error: any) {
          console.error('推进阶段失败:', error);
          message.error(error?.message || '推进阶段失败');
        }
      },
      onCancel: hideConfirmModal,
    });
  };

  return (
    <div className="space-y-6">
      {/* 页面标题和控制区域 */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>进度与监控</h1>
          <p style={{ color: 'var(--text-secondary)' }}>实时可视化各版本发布与安装进度</p>
        </div>
        <div className="flex items-center space-x-4">
          <Select
            value={selectedModel}
            onChange={(value) => {
              setSelectedModel(value);
              setSelectedVersion(undefined); // 切换机型时清空版本选择
            }}
            style={{ width: 180 }}
            placeholder="选择机型"
          >
            {deviceModels.map((model) => (
              <Select.Option key={model.id} value={model.id}>
                {model.name}
              </Select.Option>
            ))}
          </Select>
          <Select
            value={selectedVersion}
            onChange={(value) => {
              setSelectedVersion(value);
              const found = versions.find((v) => v.value === value);
              setSelectedVersionId(found?.id);
            }}
            style={{ width: 200 }}
            placeholder="选择版本"
            disabled={!selectedModel || versions.length === 0}
          >
            {versions.map((version) => (
              <Select.Option key={version.value} value={version.value}>
                {version.label}
              </Select.Option>
            ))}
          </Select>
          <Button 
            type="primary" 
            icon={<ArrowUpOutlined />} 
            onClick={handleAdvancePhase}
            disabled={!rolloutStat || !rolloutStat.isRolloutEnabled || !rolloutStat.canAdvance}
          >
            推进阶段
          </Button>
        </div>
      </div>

      {/* 关键指标卡片 */}
      <Row gutter={[24, 24]}>
        {/* 命中设备总数 */}
        <Col span={8}>
          <Card style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border-primary)' }}>
            <div className="flex items-center justify-between mb-4">
              <span className="font-medium text-lg" style={{ color: 'var(--text-primary)' }}>命中设备总数</span>
              <FileTextOutlined className="text-xl" style={{ color: 'var(--color-primary)' }} />
            </div>
            <div className="font-bold text-3xl mb-2" style={{ color: 'var(--text-primary)' }}>{versionData.totalDevices.toLocaleString()}</div>
            <div className="text-sm" style={{ color: 'var(--color-success)' }}>+2.1% 比昨天</div>
          </Card>
        </Col>

        {/* 下载成功率 */}
        <Col span={8}>
          <Card style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border-primary)' }}>
            <div className="flex items-center justify-between mb-4">
              <span className="font-medium text-lg" style={{ color: 'var(--text-primary)' }}>下载成功率</span>
              <DownloadOutlined className="text-xl" style={{ color: 'var(--color-primary)' }} />
            </div>
            <div className="font-bold text-3xl mb-3" style={{ color: 'var(--text-primary)' }}>{Number(versionData.downloadSuccessRate).toFixed(1)}%</div>
            <Progress 
              percent={Number(Number(versionData.downloadSuccessRate).toFixed(1))} 
              strokeColor="var(--color-primary)"
              trailColor="var(--border-secondary)"
              size="small"
            />
          </Card>
        </Col>

        {/* 安装成功率 */}
        <Col span={8}>
          <Card style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border-primary)' }}>
            <div className="flex items-center justify-between mb-4">
              <span className="font-medium text-lg" style={{ color: 'var(--text-primary)' }}>安装成功率</span>
              <CheckOutlined className="text-xl" style={{ color: 'var(--color-success)' }} />
            </div>
            <div className="font-bold text-3xl mb-3" style={{ color: 'var(--text-primary)' }}>{Number(versionData.installSuccessRate).toFixed(1)}%</div>
            <Progress 
              percent={Number(Number(versionData.installSuccessRate).toFixed(1))} 
              strokeColor="var(--color-success)"
              trailColor="var(--border-secondary)"
              size="small"
            />
          </Card>
        </Col>
      </Row>

      {/* 图表区域 */}
      <Row gutter={[24, 24]} className="items-stretch">
        {/* 发布进度趋势 */}
        <Col span={16}>
          <Card 
            title="发布进度趋势"
            className="h-full"
            style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border-primary)' }}
            styles={{ 
              header: { 
                background: 'var(--bg-tertiary)', 
                borderBottom: '1px solid var(--border-primary)',
                color: 'var(--text-primary)'
              }
            }}
          >
            <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>按时间维度显示检查数、下载数、成功数变化</p>
            <div className="mb-4">
              <ResponsiveContainer width="100%" height={300}>
              <LineChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-secondary)" />
                <XAxis 
                  dataKey="time" 
                  stroke="var(--text-secondary)"
                  fontSize={12}
                />
                <YAxis 
                  stroke="var(--text-secondary)"
                  fontSize={12}
                  tickFormatter={(value) => `${(value / 1000).toFixed(1)}k`}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'var(--bg-tertiary)',
                    border: '1px solid var(--border-primary)',
                    borderRadius: '6px',
                    color: 'var(--text-primary)'
                  }}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="checkCount" 
                  stroke="#1890ff" 
                  strokeWidth={2}
                  name="检查数"
                  dot={{ fill: '#1890ff', strokeWidth: 2, r: 4 }}
                />
                <Line 
                  type="monotone" 
                  dataKey="downloadCount" 
                  stroke="#52c41a" 
                  strokeWidth={2}
                  name="下载数"
                  dot={{ fill: '#52c41a', strokeWidth: 2, r: 4 }}
                />
                <Line 
                  type="monotone" 
                  dataKey="installCount" 
                  stroke="#ff7a45" 
                  strokeWidth={2}
                  name="安装数"
                  dot={{ fill: '#ff7a45', strokeWidth: 2, r: 4 }}
                />
              </LineChart>
              </ResponsiveContainer>
            </div>
            
            {/* 统计信息 */}
            <div className="grid grid-cols-3 gap-4 mt-4">
              <div className="p-3 rounded-lg" style={{ background: 'var(--bg-tertiary)' }}>
                <div className="text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>总检查数</div>
                <div className="font-bold text-lg" style={{ color: 'var(--text-primary)' }}>
                  {versionProgress?.hitDeviceTotal?.toLocaleString() || 0}
                </div>
              </div>
              <div className="p-3 rounded-lg" style={{ background: 'var(--bg-tertiary)' }}>
                <div className="text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>总下载数</div>
                <div className="font-bold text-lg" style={{ color: 'var(--text-primary)' }}>
                  {versionProgress?.downloadTotal?.toLocaleString() || 0}
                </div>
              </div>
              <div className="p-3 rounded-lg" style={{ background: 'var(--bg-tertiary)' }}>
                <div className="text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>总安装数</div>
                <div className="font-bold text-lg" style={{ color: 'var(--text-primary)' }}>
                  {versionProgress?.installTotal?.toLocaleString() || 0}
                </div>
              </div>
            </div>
          </Card>
        </Col>

        {/* 错误类型分布 */}
        <Col span={8}>
          <Card 
            title="错误类型分布"
            className="h-full"
            style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border-primary)' }}
            styles={{ 
              header: { 
                background: 'var(--bg-tertiary)', 
                borderBottom: '1px solid var(--border-primary)',
                color: 'var(--text-primary)'
              }
            }}
          >
            <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>各类错误的占比统计</p>
            <div className="mb-4">
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={errorData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {errorData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value, name) => [`${Number(value ?? 0).toFixed(1)}%`, name]}
                    labelFormatter={(label: string) => `${label}`}
                    contentStyle={{
                      backgroundColor: 'var(--bg-tertiary)',
                      border: '1px solid var(--border-primary)',
                      borderRadius: '6px',
                      color: 'var(--text-primary) !important'
                    }}
                    itemStyle={{
                      color: 'var(--text-primary) !important'
                    }}
                    labelStyle={{
                      color: 'var(--text-primary) !important'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            
            {/* 图例 */}
            <div className="space-y-2">
              {errorData.map((item, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div 
                      className="w-3 h-3 rounded-full mr-2" 
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="text-sm" style={{ color: 'var(--text-primary)' }}>{item.name}</span>
                  </div>
                  <span className="font-medium" style={{ color: 'var(--text-primary)' }}>{Number(item.value).toFixed(1)}%</span>
                </div>
              ))}
            </div>
          </Card>
        </Col>
      </Row>

      {/* 灰度发布进度 */}
      <Card 
        title={
          <div className="flex items-center">
            <ArrowUpOutlined className="mr-2" style={{ color: 'var(--color-primary)' }} />
            <span>灰度发布进度</span>
          </div>
        }
        style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border-primary)' }}
        styles={{ 
          header: { 
            background: 'var(--bg-tertiary)', 
            borderBottom: '1px solid var(--border-primary)',
            color: 'var(--text-primary)'
          }
        }}
        extra={
          rolloutStat?.isRolloutEnabled ? (
            <Button type="primary" onClick={handleAdvancePhase} disabled={!rolloutStat?.canAdvance}>
              手动推进
            </Button>
          ) : null
        }
      >
        {rolloutStat && !rolloutStat.isRolloutEnabled ? (
          <div className="py-8 text-center">
            <p style={{ color: 'var(--text-secondary)' }}>当前版本未配置灰度发布策略。</p>
            <p style={{ color: 'var(--text-tertiary)', marginTop: 8 }}>
              请在版本管理页面为该版本配置发布策略后再查看灰度进度。
            </p>
          </div>
        ) : (
          <div className="mb-4">
            <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>
              分阶段推进发布范围，当前处于阶段{versionData.currentPhase || 0}
            </p>
            
            {/* 整体进度条 */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <span style={{ color: 'var(--text-primary)' }}>当前灰度比例</span>
                <span className="font-bold" style={{ color: 'var(--color-primary)' }}>
                  {Number(versionData.progress).toFixed(1)}%
                </span>
              </div>
              <Progress 
                percent={Number(Number(versionData.progress).toFixed(1))} 
                strokeColor="var(--color-primary)"
                trailColor="var(--border-secondary)"
                className="mb-4"
              />
            </div>

            {/* 阶段时间线 */}
            <div className="space-y-6">
              {phases.map((phase) => (
                <div key={phase.phase} className="flex items-start">
                  {/* 阶段图标 */}
                  <div className="flex-shrink-0 mr-4 mt-1">
                    {getPhaseIcon(phase.status, phase.phase)}
                  </div>
                  
                  {/* 阶段内容 */}
                  <div className="flex-1">
                    <div className="flex items-center mb-1">
                      <span className="font-medium text-base mr-4" style={{ color: 'var(--text-primary)' }}>
                        阶段{phase.phase}
                      </span>
                      <div
                        className="px-3 py-1 rounded-md text-sm font-medium mr-4"
                        style={{ background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
                      >
                        {Number(phase.percentage).toFixed(1)}% 设备
                      </div>
                      <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                        {phase.description}
                      </span>
                    </div>
                    
                    {/* 当前阶段的额外信息 */}
                    {phase.status === 'current' && (
                      <div className="mt-2">
                        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                          等待手动推进到下一阶段
                        </p>
                      </div>
                    )}
                    
                    {/* 已完成阶段的时间信息 */}
                    {phase.status === 'completed' && phase.startTime && (
                      <div className="mt-1">
                        <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                          {phase.startTime}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* 全局确认框 */}
      <ConfirmModal 
        config={{
          ...config,
          onConfirm: handleConfirm,
          onCancel: handleCancel,
        }}
      />
    </div>
  );
};

export default ProgressMonitoring;
